import { useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { initDaywellImageReveals } from '@/lib/daywellImageReveal';

export default function SiteLayout() {
  const location = useLocation();
  const { pathname, hash, key } = location;
  const navType = useNavigationType();
  const positions = useRef<Map<string, number>>(new Map());

  // Let us manage restoration so it doesn't fight the pinned Hero.
  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  // Remember where the user was on the page they're leaving.
  useEffect(() => {
    const map = positions.current;
    return () => {
      map.set(key, window.scrollY);
    };
  }, [key]);

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView();
        return undefined;
      }
    }
    if (navType === 'POP' && positions.current.has(key)) {
      const y = positions.current.get(key) ?? 0;
      window.scrollTo(0, 0);
      // Wait for pinned/sticky layout heights to be applied before restoring.
      let r2 = 0;
      const r1 = window.requestAnimationFrame(() => {
        r2 = window.requestAnimationFrame(() => window.scrollTo(0, y));
      });
      return () => {
        window.cancelAnimationFrame(r1);
        window.cancelAnimationFrame(r2);
      };
    }
    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash, key, navType]);

  // Global image reveals: one instance per route, initialised after scroll reset
  // so the "already on screen" check is accurate. Cleaned up on route change.
  useEffect(() => initDaywellImageReveals(document), [pathname]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950 overflow-x-clip">
      <Header />
      {/* Opacity-only route entrance; keyed so each page fades in once. */}
      <main key={pathname} className="dw-page-enter">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}